import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CHAT_REACTION_EMOJIS } from '@/config';

interface ChatReactionPickerProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

interface Position {
  top: number;
  left: number;
}

export default function ChatReactionPicker({ anchorRef, onSelect, onClose }: ChatReactionPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Position | null>(null);

  // Compute position based on the trigger button, placing the picker above
  // and right-aligned to the anchor. Clamp within viewport.
  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const picker = ref.current;
      if (anchor === null || picker === null) return;

      const anchorRect = anchor.getBoundingClientRect();
      const pickerRect = picker.getBoundingClientRect();
      const margin = 8;

      let top = anchorRect.top - pickerRect.height - margin;
      if (top < margin) {
        // Not enough room above; place below.
        top = anchorRect.bottom + margin;
      }

      let left = anchorRect.right - pickerRect.width;
      if (left < margin) left = margin;
      const maxLeft = window.innerWidth - pickerRect.width - margin;
      if (left > maxLeft) left = maxLeft;

      setPos({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        // Also ignore clicks on the anchor (the button that opened us).
        const anchor = anchorRef.current;
        if (anchor !== null && anchor.contains(e.target as Node)) return;
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, anchorRef]);

  const picker = (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        visibility: pos === null ? 'hidden' : 'visible',
      }}
      className="z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 grid grid-cols-5 gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {CHAT_REACTION_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          onClick={() => { onSelect(emoji); onClose(); }}
          className="text-xl p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors leading-none"
          title={`Reagieren mit ${emoji}`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );

  return createPortal(picker, document.body);
}
